import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  PaEmptyState,
  PaMetricCard,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  fetchPlatformAuditLog,
  fetchPlatformTenantIntegrations,
  fetchPlatformTenantUsage,
} from "store/services/platformAdmin.services";
import { buildAuditLogQuery, createDefaultAuditFilters } from "../Audit/auditLog.utils";
import {
  buildTenantAlerts,
  buildTenantQuickLinks,
  formatSubscriptionStatusLabel,
  formatTenantActivityRow,
  getSubscriptionStatusVariant,
  summarizeIntegrations,
  summarizeUsageModules,
} from "./tenantOverview.utils";
import { formatParticipantLimitUsage } from "./tenantBilling.utils";
import { formatCareSectorLabel, getTenantStatusBadge } from "./tenantsList.utils";

const DetailRow = ({ label, children }) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 py-3 last:border-b-0 sm:flex-row sm:justify-between">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 sm:text-right">{children}</dd>
  </div>
);

const FlagBadge = ({ active, activeLabel, inactiveLabel }) => (
  <PaStatusBadge
    label={active ? activeLabel : inactiveLabel}
    variant={active ? "warning" : "neutral"}
  />
);

const AlertBanner = ({ alert }) => {
  const styles = {
    danger: "border-red-200 bg-red-50 text-red-800",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    neutral: "border-gray-200 bg-gray-50 text-gray-800",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${styles[alert.variant] || styles.neutral}`}>
      <p className="text-sm font-medium">{alert.title}</p>
      {alert.message ? <p className="mt-1 text-xs opacity-90">{alert.message}</p> : null}
    </div>
  );
};

const SectionHeader = ({ title, action }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {action}
  </div>
);

const TenantOverviewTab = ({ tenant, tenantId, onNavigateTab, capabilities }) => {
  const resolvedCapabilities = capabilities || {};
  const canViewAudit = resolvedCapabilities.canViewAudit ?? platformRoleAccess("platform.audit.view");
  const canViewIntegrations =
    resolvedCapabilities.canViewIntegrations ?? platformRoleAccess("platform.integrations.view");
  const canViewUsage = resolvedCapabilities.canViewUsage ?? platformRoleAccess("platform.usage.view");
  const canViewBilling =
    resolvedCapabilities.canViewBilling ?? platformRoleAccess("platform.billing.view");
  const canViewTenantUsers =
    resolvedCapabilities.canViewTenantUsers ??
    (platformRoleAccess("platform.user.search") ||
      platformRoleAccess("platform.tenant.users.create"));

  const [loadingExtras, setLoadingExtras] = useState(true);
  const [usage, setUsage] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  const loadOverviewExtras = useCallback(async () => {
    if (!tenantId) {
      setLoadingExtras(false);
      return;
    }

    setLoadingExtras(true);

    const tasks = [];

    if (canViewUsage) {
      tasks.push(
        fetchPlatformTenantUsage(tenantId)
          .then((data) => setUsage(data))
          .catch(() => setUsage(null))
      );
    }

    if (canViewIntegrations) {
      tasks.push(
        fetchPlatformTenantIntegrations(tenantId)
          .then((data) => setIntegrations(data?.integrations ?? {}))
          .catch(() => setIntegrations({}))
      );
    }

    if (canViewAudit) {
      const filters = {
        ...createDefaultAuditFilters(),
        spId: String(tenantId),
        page: 1,
        perPage: 5,
      };

      tasks.push(
        fetchPlatformAuditLog(buildAuditLogQuery(filters))
          .then((data) => setRecentActivity(data?.audit_log?.data || []))
          .catch(() => setRecentActivity([]))
      );
    }

    await Promise.all(tasks);
    setLoadingExtras(false);
  }, [canViewAudit, canViewIntegrations, canViewUsage, tenantId]);

  useEffect(() => {
    loadOverviewExtras();
  }, [loadOverviewExtras]);

  const alerts = useMemo(
    () => buildTenantAlerts(tenant, { canViewBilling }),
    [tenant, canViewBilling]
  );
  const usageSummary = useMemo(() => summarizeUsageModules(usage), [usage]);
  const integrationSummary = useMemo(
    () => summarizeIntegrations({ integrations }),
    [integrations]
  );
  const activityRows = useMemo(
    () => recentActivity.map(formatTenantActivityRow),
    [recentActivity]
  );

  const quickLinks = useMemo(
    () =>
      buildTenantQuickLinks({
        canViewAudit,
        canViewUsage,
        canViewIntegrations,
        canViewBilling,
        canViewTenantUsers,
      }),
    [canViewAudit, canViewBilling, canViewIntegrations, canViewTenantUsers, canViewUsage]
  );

  if (!tenant) {
    return null;
  }

  const statusBadge = getTenantStatusBadge(tenant.status);
  const subscription = tenant.subscription || {};
  const billing = tenant.billing || {};
  const participantLimit = billing.participant_limit || {};
  const flags = tenant.flags || {};
  const counts = tenant.counts || {};
  const billingVariant = getSubscriptionStatusVariant(subscription, flags);

  return (
    <div className="space-y-6">
      {alerts.length > 0 ? (
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {alerts.map((alert) => (
            <AlertBanner key={alert.id} alert={alert} />
          ))}
        </section>
      ) : null}

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <PaMetricCard label="Participants" value={counts.participants ?? "—"} />
        {canViewBilling &&
        participantLimit.plan_limit !== null &&
        participantLimit.plan_limit !== undefined ? (
          <PaMetricCard
            label="Plan usage"
            value={formatParticipantLimitUsage(participantLimit)}
          />
        ) : null}
        <PaMetricCard label="Workers" value={counts.workers ?? "—"} />
        <PaMetricCard label="Managers" value={counts.managers ?? "—"} />
        <PaMetricCard
          label="Module adoption"
          value={
            loadingExtras && canViewUsage
              ? "…"
              : usageSummary.averageScore !== null
                ? `${usageSummary.averageScore}% avg`
                : "—"
          }
        />
        <PaMetricCard
          label="Integrations"
          value={
            loadingExtras && canViewIntegrations
              ? "…"
              : integrationSummary.total
                ? `${integrationSummary.connected}/${integrationSummary.total} live`
                : "—"
          }
        />
        <PaMetricCard label="Regions" value={tenant.regions?.length ?? "—"} />
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionHeader title="Organisation" />
            <dl>
              <DetailRow label="Business name">{tenant.name}</DetailRow>
              <DetailRow label="Tenant email">{tenant.email || "—"}</DetailRow>
              <DetailRow label="ABN">
                <span className="font-mono">{tenant.abn || "—"}</span>
              </DetailRow>
              <DetailRow label="Sector">{formatCareSectorLabel(tenant.care_sector)}</DetailRow>
              <DetailRow label="Status">
                <PaStatusBadge label={statusBadge.label} variant={statusBadge.variant} />
              </DetailRow>
              <DetailRow label="Created">
                {tenant.created_at
                  ? moment(tenant.created_at).format("D MMM YYYY, h:mm a")
                  : "—"}
              </DetailRow>
              <DetailRow label="Last activity">
                {tenant.last_login_at ? moment(tenant.last_login_at).fromNow() : "—"}
              </DetailRow>
            </dl>
          </section>

          {canViewBilling ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader
                title="Subscription & billing"
                action={
                  onNavigateTab ? (
                    <button
                      type="button"
                      onClick={() => onNavigateTab("billing")}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Full billing →
                    </button>
                  ) : null
                }
              />
              <dl>
                <DetailRow label="Plan">{subscription.plan_name || "—"}</DetailRow>
                <DetailRow label="Billing status">
                  <PaStatusBadge
                    label={formatSubscriptionStatusLabel(subscription)}
                    variant={billingVariant}
                  />
                </DetailRow>
                {participantLimit.plan_limit !== null &&
                participantLimit.plan_limit !== undefined ? (
                  <DetailRow label="Participant plan usage">
                    <PaStatusBadge
                      label={formatParticipantLimitUsage(participantLimit)}
                      variant={
                        participantLimit.read_only_due_to_participant_limit
                          ? "danger"
                          : participantLimit.over_limit
                            ? "warning"
                            : "success"
                      }
                    />
                  </DetailRow>
                ) : null}
                <DetailRow label="Trial">
                  {subscription.on_trial ? (
                    <PaStatusBadge label="On trial" variant="warning" />
                  ) : (
                    "Not on trial"
                  )}
                </DetailRow>
                {subscription.trial_end_date ? (
                  <DetailRow label="Trial ends">
                    {moment(subscription.trial_end_date).format("D MMM YYYY")}
                  </DetailRow>
                ) : null}
                {subscription.stripe_dashboard_customer_url ? (
                  <DetailRow label="Stripe customer">
                    <a
                      href={subscription.stripe_dashboard_customer_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      Open in Stripe
                    </a>
                  </DetailRow>
                ) : null}
                {subscription.stripe_dashboard_subscription_url ? (
                  <DetailRow label="Stripe subscription">
                    <a
                      href={subscription.stripe_dashboard_subscription_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      Open in Stripe
                    </a>
                  </DetailRow>
                ) : null}
              </dl>
            </section>
          ) : null}

          {canViewUsage ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader
                title="Module adoption"
                action={
                  onNavigateTab ? (
                    <button
                      type="button"
                      onClick={() => onNavigateTab("usage")}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      View usage →
                    </button>
                  ) : null
                }
              />
              {loadingExtras ? (
                <PaSkeleton variant="text" lines={4} />
              ) : usageSummary.rows.length === 0 ? (
                <PaEmptyState
                  title="No usage snapshot"
                  message="Refresh usage from the Usage tab to populate module scores."
                />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {usageSummary.rows.slice(0, 6).map((module) => (
                    <li
                      key={module.key}
                      className="flex items-center justify-between gap-3 py-2 text-sm"
                    >
                      <span className="text-gray-700">{module.label}</span>
                      <span className="font-medium text-gray-900">
                        {module.score === null || module.score === undefined
                          ? "—"
                          : `${module.score}%`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {canViewIntegrations ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader
                title="Integrations"
                action={
                  onNavigateTab ? (
                    <button
                      type="button"
                      onClick={() => onNavigateTab("integrations")}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      View all →
                    </button>
                  ) : null
                }
              />
              {loadingExtras ? (
                <PaSkeleton variant="text" lines={3} />
              ) : integrationSummary.rows.length === 0 ? (
                <p className="text-sm text-gray-500">No integrations configured.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {integrationSummary.rows.map((row) => (
                    <li
                      key={row.key}
                      className="flex items-center justify-between gap-3 py-2 text-sm"
                    >
                      <span className="text-gray-700">{row.label}</span>
                      <PaStatusBadge
                        label={row.statusBadge.label}
                        variant={row.statusBadge.variant}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>

        <div className="space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionHeader title="Owner" />
            <dl>
              <DetailRow label="Name">{tenant.owner?.name || "—"}</DetailRow>
              <DetailRow label="Email">{tenant.owner?.email || "—"}</DetailRow>
            </dl>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <SectionHeader title="Flags" />
            <div className="flex flex-wrap gap-2">
              <FlagBadge
                active={flags.read_only}
                activeLabel="Read-only"
                inactiveLabel="Writable"
              />
              <FlagBadge
                active={flags.wildcard}
                activeLabel="Wildcard"
                inactiveLabel="Standard billing"
              />
              <FlagBadge
                active={flags.on_trial}
                activeLabel="Trial"
                inactiveLabel="Paid"
              />
            </div>
          </section>

          {tenant.regions?.length ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader title="Regions" />
              <ul className="space-y-2">
                {tenant.regions.map((region) => (
                  <li key={region.id} className="text-sm text-gray-700">
                    {region.name}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {canViewAudit ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader
                title="Recent activity"
                action={
                  onNavigateTab ? (
                    <button
                      type="button"
                      onClick={() => onNavigateTab("activity")}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      View all →
                    </button>
                  ) : (
                    <Link
                      to={`/audit-log?sp_id=${tenantId}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Audit log →
                    </Link>
                  )
                }
              />
              {loadingExtras ? (
                <PaSkeleton variant="text" lines={4} />
              ) : activityRows.length === 0 ? (
                <p className="text-sm text-gray-500">No recent platform activity.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {activityRows.map((row) => (
                    <li key={row.id} className="py-3">
                      <p className="text-sm font-medium text-gray-900">{row.primary}</p>
                      <p className="mt-1 text-xs text-gray-500">{row.secondary}</p>
                      {row.createdAt ? (
                        <time
                          className="mt-1 block text-xs text-gray-400"
                          dateTime={row.createdAt}
                        >
                          {moment(row.createdAt).fromNow()}
                        </time>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {quickLinks.length > 0 ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <SectionHeader title="Quick links" />
              <div className="flex flex-col gap-2">
                {quickLinks.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => onNavigateTab?.(link.action)}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TenantOverviewTab;
